(ns dre.components.searchbar.views
  (:require
   [dre.app.events :as-alias app]
   [dre.components.caret :refer [caret]]
   [dre.components.icon :as icon]
   [dre.components.pill :refer [pills] :rename {pills pills-component}]
   [dre.components.searchbar.events :as events]
   [dre.components.searchbar.filters :refer [filters]]
   [dre.components.searchbar.subs :as subs]
   [dre.components.searchbar.suggestion-dropdown :refer [suggestion-dropdown]]
   [dre.i18n.views :refer [tr]]
   [dre.pages.my-searches.tags.events :as-alias utag-events]
   [dre.utils.bem :as bem-utils]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(def component-name :searchbar)

(defn- be
  [e]
  (bem-utils/be component-name e))

(defn- bem
  [e m]
  (bem-utils/bem component-name e m))

(defn- search-input-hint
  [{:keys [db-root-path hint show-hint?] :as opts}]
  (when (show-hint? opts)
    [:div
     {:class (be :search-input-hint)
      :on-click #(when (= (.-currentTarget %)
                          (.-target %)) 
                   (rf/dispatch [::events/set-caret-position-end db-root-path]))}
     @hint]))

(defn- search-input-reset-query
  [{:keys [hide-reset-query? clear-query pill-count]}]
  (when (and
         (not= hide-reset-query? true)
         (pos? pill-count))
    [:div
     {:class (be :search-input-reset-query)
      :on-click clear-query}
     [icon/fa-icon :x-mark]]))

(defn- search-input-extra
  [{:keys [focus-on-caret show-hint? hide-reset-query? pill-count] :as opts}]
  (when (or
         (show-hint? opts)
         (and
          (not= hide-reset-query? true)
          (pos? pill-count)))
    [:div
     {:class (be :search-input-extra)
      :on-click focus-on-caret}
     [search-input-hint opts]
     [search-input-reset-query opts]]))

(defn- search-input
  [{:keys [tags user-profile placeholder show-suffix-icon? searchbar-ref db-root-path] :as opts}]
  (let [pills @(rf/subscribe [::subs/pills db-root-path])
        focus-on-caret #(when (= (.-currentTarget %)
                                 (.-target %)) 
                          (rf/dispatch [::events/set-caret-position db-root-path -1]))]
    [:div
     {:class [(be :search-input)]
      :id (events/generate-search-input-id db-root-path)}
     [:div
      {:class (be :search-input-content)
       :on-click focus-on-caret
       :ref (fn [e] (when searchbar-ref
                      (reset! searchbar-ref e)))}
      (if @(rf/subscribe [::subs/pills-empty? db-root-path])
        [:<>
         [caret (assoc opts :i 0)]
         (when (and
                @(rf/subscribe [::subs/caret-position-nil? db-root-path])
                (some? placeholder))
           [:div
            {:class (be :search-input-placeholder)
             :on-click #(rf/dispatch [::events/set-caret-position db-root-path 0])}
            placeholder])]
        [pills-component pills {:render-caret? true
                                :caret-opts (assoc opts :i nil)
                                :tags @tags
                                :user-profile @user-profile
                                :focus-on-caret focus-on-caret}])]
     [search-input-extra (assoc opts
                                :focus-on-caret focus-on-caret)]
     (when show-suffix-icon?
       [:div
        {:class (be :search-input-suffix-icon)}
        [icon/icon :common :search]])]))

(defn- search-input-container
  [_]
  (let [active-suggestion-idx (r/atom 0)]
    (fn [opts]
      (let [opts (assoc opts
                        :active-suggestion-idx active-suggestion-idx)]
        [:div
         {:class (be :search-input-container)}
         [search-input opts]
         [suggestion-dropdown opts]]))))

(defn- button-search
  [{:keys [on-click disabled?]}]
  [:div
   {:class [(be :button)
            (bem :button :search)
            (when disabled? (bem :button :disabled))]
    :on-click (when-not disabled? on-click)}
   [icon/icon :common :search]])

(defn- button-undo
  [{:keys [on-click disabled?]}]
  (let [disabled? @disabled?]
    [:div
     {:class [(be :button)
              (bem :button :undo)
              (when disabled? (bem :button :disabled))]
      :on-click (when-not disabled?
                  on-click)}
     [icon/icon :common :undo]]))

(defn- button-redo
  [{:keys [on-click disabled?]}]
  (let [disabled? @disabled?]
    [:div
     {:class [(be :button)
              (bem :button :redo)
              (when disabled? (bem :button :disabled))]
      :on-click (when-not disabled?
                  on-click)}
     [icon/icon :common :redo]]))

(defn- button-optimize
  [{:keys [disabled?] :as opts}]
  (let [
        button [:div
                {:class [(be :button)
                         (bem :button :optimize)
                         (when disabled? (bem :button :disabled))]}
                [tr "Optimize"]]]
    (if disabled?
      button
      [:a
       {:href "https://share.hsforms.com/1suIB-3XFS5WI2Os9DP6n1w5a1xb"
        :target "_blank"}
       button])))

(defn- mock-submit-on-enter-for-heap
  [db-root-path]
  ;; This will let heap know when query is submitted by pressing the enter key
  ;; See dre.components.searchbar.main-searchbar on-submit callback
  [:div.mock-submit-on-enter-for-heap
   {:id (->> db-root-path (map name) (apply str))
    :style {:display :none}}])

(defn searchbar
  [{:keys [db-root-path] :as opts}]
  (let [caret-position @(rf/subscribe [::subs/caret-position db-root-path])
        pill-count @(rf/subscribe [::subs/pill-count db-root-path]) 
        defaults {:on-submit (constantly nil)
                  :on-undo (constantly nil)
                  :on-redo (constantly nil)
                  :on-type (constantly nil)
                  :remove-pill (constantly nil)
                  :on-change (constantly nil)
                  :placeholder "Enter and select individual terms/concepts here."
                  :show-buttons? true
                  :show-optimize-button? true
                  :undo-disabled? (r/atom true)
                  :redo-disabled? (r/atom false)
                  :tags (rf/subscribe [::utag-events/tags-without-filter])
                  :user-profile (rf/subscribe [::app/user])
                  :show-hint? (fn [{:keys [pill-count hint]}]
                                (and
                                 (pos? pill-count)
                                 (some? @hint)))
                  :show-filters? true
                  :searchbar-ref nil 
                  :search-disabled? (rf/subscribe [::subs/search-disabled? db-root-path])
                  :suggestion-tooltip-placement "bottom"}
        opts (assoc (merge defaults opts)
                    :pill-count pill-count
                    :caret-position caret-position)
        {:keys [on-submit on-undo undo-disabled? on-redo redo-disabled?
                show-buttons? show-optimize-button? show-filters? search-disabled?]} opts]
    [:div
     {:class component-name}
     [mock-submit-on-enter-for-heap db-root-path]
     [:div
      {:class (be :search-bar-row)}
      [search-input-container opts]
      (when show-buttons?
        [:<>
         [button-search
          {:on-click on-submit
           :disabled? @search-disabled?}]
         [button-undo
          {:on-click on-undo
           :disabled? undo-disabled?}]
         [button-redo
          {:on-click on-redo
           :disabled? redo-disabled?}]
         (when show-optimize-button?
           [button-optimize
            {:disabled? @search-disabled?}])])]
     (when show-filters?
       [filters opts])]))
